import { useState, useEffect, useCallback } from 'react';

import { AxiosError } from 'axios';

import { ConfigOptionsApi } from '@/api';
import { IFieldItem } from '@/layouts/TabLayout/MasterNames/data';
import {
  IDynamicFormTypes,
  IOffenseOption,
  IOffenseOptionItem,
  IOffenseOptionResponse,
} from '@/models';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils';

export interface IFormConfigLoader {
  formName?: IDynamicFormTypes;
  optionsName?: string;
  category?: string | string[];
}
export const useFormConfigLoader = ({
  formName,
  optionsName,
  category,
}: IFormConfigLoader) => {
  const [config, setConfig] = useState<IFieldItem[] | null>(null);
  const [allOptions, setAllOptions] = useState<IOffenseOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<IOffenseOption[]>([]);
  // Default to true so consumers that gate on `loading` (e.g. `ArrestsToolDataGrid`)
  // don't mount their child UI before the first load cycle has had a chance to run.
  // Without this, the initial render shows the gated child, the loader effect then
  // flips loading=true → unmount → load → loading=false → remount, causing two fetches.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleError = (err: unknown) => {
    // eslint-disable-next-line no-console
    console.error(err);
    if (err instanceof AxiosError) {
      setError(err.message);
    } else {
      setError('An unexpected error occurred');
    }
  };
  // TODO: ivica, when more time,  maybe merge or atleast refactor loadCOnfig and loadOption, too simmilar
  const loadConfig = useCallback(async () => {
    if (!formName) return;

    setLoading(true);
    setError(null);

    try {
      let savedFormOptions = getLocalStorageItem({
        key: formName,
        shouldParse: true,
      });

      const isOldVersion = await shoudFetchNewVersion(
        savedFormOptions,
        formName,
        'formType',
      );
      if (isOldVersion || !savedFormOptions) {
        const { data } = await ConfigOptionsApi.getFormConfig(formName);
        setLocalStorageItem({ key: formName, value: data });
        savedFormOptions = data;
      }

      setConfig(
        savedFormOptions.filter(
          (fieldItem: { name?: string }) => fieldItem?.name,
        ),
      );
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [formName]);

  const loadOptions = useCallback(async () => {
    if (!optionsName) return;

    setLoading(true);
    setError(null);

    try {
      let savedOptions = getLocalStorageItem({
        key: `${optionsName}_options`,
        shouldParse: true,
      });

      const isOldVersion = await shoudFetchNewVersion(
        savedOptions,
        optionsName,
        'optionType',
      );

      if (isOldVersion || !savedOptions) {
        const { data } = await ConfigOptionsApi.getConfigOptions(optionsName);
        setLocalStorageItem({
          key: `${optionsName}_options`,
          value: data,
        });
        savedOptions = data;
      }

      const properKeys = savedOptions.map(
        (savedOption: IOffenseOptionResponse) => {
          return {
            ...savedOption,
            items: savedOption.items?.reduce((acc, item) => {
              acc.set(item.value, item);
              return acc;
            }, new Map<string, IOffenseOptionItem>()),
          };
        },
      );
      setAllOptions(properKeys);

      if (category) {
        if (Array.isArray(category)) {
          for (const cat of category) {
            setFilteredOptions((state) => [
              ...state,
              ...getFilteredOptions(cat, properKeys),
            ]);
          }
        } else {
          setFilteredOptions(getFilteredOptions(category, properKeys));
        }
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [optionsName]);

  const shoudFetchNewVersion = async (
    savedOptions: any,
    fileName: string,
    fileType: 'optionType' | 'formType',
  ) => {
    if (!savedOptions) return true;
    const localVersion =
      savedOptions.find((item: { VERSION?: number }) => item.VERSION) || -1;
    const serverVersion = await ConfigOptionsApi.getFileVersion(
      fileName,
      fileType,
    );

    return localVersion.VERSION < serverVersion.data;
  };
  useEffect(() => {
    loadConfig();
    loadOptions();
  }, [loadConfig, loadOptions]);

  const getFilteredOptions = useCallback(
    (cat: string, formOptions?: IOffenseOption[]) => {
      const options = (formOptions ? formOptions : allOptions)?.filter(
        (option) => option.category?.includes(cat),
      );
      return options;
    },
    [allOptions],
  );

  return {
    config,
    loading,
    error,
    allOptions,
    filteredOptions,
  };
};
